import { Plate } from '../components/Card';
import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';

const fmt = v => new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(v || 0);

const statusMeta = {
  active:  { label: 'Активен', color: '#0e9f6e', bg: 'rgba(14,159,110,0.12)' },
  warning: { label: 'Предупреждение', color: '#e08a00', bg: 'rgba(224,138,0,0.12)' },
  limited: { label: 'Ограничен', color: '#e08a00', bg: 'rgba(224,138,0,0.12)' },
  penalty: { label: 'Двойной тариф', color: '#e04848', bg: 'rgba(224,72,72,0.12)' },
  blocked: { label: 'Заблокирован', color: '#e04848', bg: 'rgba(224,72,72,0.18)' },
};

const cardStyle = {
  background: 'var(--bg2)',
  border: '1px solid var(--border)',
  borderRadius: 14,
  padding: 16,
};

const inputStyle = {
  width: '100%',
  padding: '9px 10px',
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--bg3)',
  color: 'var(--text1)',
  fontSize: 13,
  boxSizing: 'border-box',
};

const buttonStyle = {
  border: 'none',
  borderRadius: 9,
  padding: '9px 14px',
  background: 'var(--accent)',
  color: '#fff',
  fontWeight: 800,
  cursor: 'pointer',
};

function StatusBadge({ status }) {
  const meta = statusMeta[status] || statusMeta.active;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', padding: '3px 9px',
      borderRadius: 999, background: meta.bg, color: meta.color,
      fontSize: 11, fontWeight: 800,
    }}>
      {meta.label}
    </span>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: 'grid', gap: 5, fontSize: 12, color: 'var(--text2)' }}>
      <span>{label}</span>
      {children}
    </label>
  );
}

function Section({ title, sub, children }) {
  return (
    <section style={{ ...cardStyle, marginBottom: 16 }}>
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>{title}</h2>
        {sub && <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text2)' }}>{sub}</div>}
      </div>
      {children}
    </section>
  );
}

export default function PartnerSaas() {
  const [dashboard, setDashboard] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [orgId, setOrgId] = useState('');
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadDashboard = async (preferredOrgId = orgId) => {
    setLoading(true);
    setError('');
    try {
      const data = await api.get('/partner-saas/dashboard').then(r => r.data);
      setDashboard(data);
      const nextOrgId = preferredOrgId || data.organizations?.[0]?.id || '';
      setOrgId(nextOrgId ? String(nextOrgId) : '');
      if (nextOrgId) {
        const ws = await api.get(`/partner-saas/workspace/${nextOrgId}`).then(r => r.data);
        setWorkspace(ws);
      } else {
        setWorkspace(null);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Не удалось загрузить Partner SaaS');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDashboard(); }, []);

  const selectOrg = async id => {
    setOrgId(String(id));
    setWorkspace(null);
    setError('');
    try {
      const ws = await api.get(`/partner-saas/workspace/${id}`).then(r => r.data);
      setWorkspace(ws);
    } catch (e) {
      setError(e.response?.data?.detail || 'Не удалось открыть партнёра');
    }
  };

  const runAction = async (fn, okText = 'Готово') => {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await fn();
      setMessage(okText);
      await loadDashboard(orgId);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Ошибка операции');
    } finally {
      setSaving(false);
    }
  };

  const org = workspace?.organization;
  const summary = dashboard?.summary || {};
  const products = workspace?.products || [];
  const warehouses = workspace?.warehouses || [];
  const stores = workspace?.stores || [];

  return (
    <div>
      <div className="kp-hero" style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <Plate>Франшиза · Partner SaaS</Plate>
          <h1 style={{ margin: 0 }}>Partner SaaS для розницы</h1>
          <div style={{ marginTop: 8, color: 'var(--text2)', fontSize: 13 }}>
            Склад, продажи, касса/эквайринг и контроль royalty в одном приложении.
          </div>
        </div>
        <button
          disabled={saving}
          onClick={() => runAction(() => api.post('/partner-saas/seed-demo'), 'Демо-данные готовы')}
          style={{ ...buttonStyle, background: '#0e9f6e' }}
        >
          Создать демо
        </button>
      </div>

      {message && <div style={{ ...cardStyle, marginBottom: 14, borderColor: 'rgba(14,159,110,0.35)', color: '#0e9f6e' }}>{message}</div>}
      {error && <div style={{ ...cardStyle, marginBottom: 14, borderColor: 'rgba(224,72,72,0.35)', color: '#e04848' }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(140px, 1fr))', gap: 12, marginBottom: 18 }}>
        {[
          ['Партнёры', summary.partners_count || 0, '#00a7dc'],
          ['Оборот', fmt(summary.turnover), '#0e9f6e'],
          ['К оплате', fmt(summary.total_due), '#00a7dc'],
          ['Дебиторка', fmt(summary.total_debt), summary.total_debt > 0 ? '#e04848' : '#0e9f6e'],
          ['Товаров', summary.products_count || 0, '#00a7dc'],
        ].map(([label, value, color]) => (
          <div key={label} style={cardStyle}>
            <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 5 }}>{label}</div>
            <div style={{ fontSize: 21, fontWeight: 900, color }}>{value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 18, alignItems: 'start' }}>
        <aside style={{ ...cardStyle, position: 'sticky', top: 28 }}>
          <div style={{ fontSize: 11, color: 'var(--text2)', textTransform: 'uppercase', fontWeight: 800, marginBottom: 10 }}>
            Партнёры
          </div>
          {loading ? (
            <div style={{ color: 'var(--text2)', fontSize: 13 }}>Загрузка...</div>
          ) : (dashboard?.organizations || []).length ? (
            <div style={{ display: 'grid', gap: 8 }}>
              {dashboard.organizations.map(item => (
                <button
                  key={item.id}
                  onClick={() => selectOrg(item.id)}
                  style={{
                    textAlign: 'left',
                    border: `1px solid ${String(item.id) === String(orgId) ? 'rgba(0,167,220,0.6)' : 'var(--border)'}`,
                    background: String(item.id) === String(orgId) ? 'rgba(0,167,220,0.12)' : 'var(--bg3)',
                    color: 'var(--text1)',
                    borderRadius: 10,
                    padding: 10,
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ fontWeight: 900, fontSize: 13 }}>{item.name}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', marginTop: 7 }}>
                    <StatusBadge status={item.access_status} />
                    <span style={{ fontSize: 11, color: item.current_debt > 0 ? '#e04848' : '#0e9f6e', fontWeight: 800 }}>
                      {fmt(item.current_debt)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div style={{ color: 'var(--text2)', fontSize: 13, lineHeight: 1.5 }}>
              Пока нет партнёров. Создай демо или добавь первого партнёра вручную.
            </div>
          )}

          <div style={{ height: 1, background: 'var(--border)', margin: '14px 0' }} />
          {[
            ['overview', 'Обзор'],
            ['partner', 'Партнёр и тариф'],
            ['inventory', 'Склад'],
            ['sales', 'Продажи'],
            ['billing', 'Billing и долги'],
            ['integrations', 'Касса и эквайринг'],
            ['audit', 'Аудит'],
          ].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              style={{
                width: '100%',
                textAlign: 'left',
                border: 'none',
                borderRadius: 8,
                padding: '9px 10px',
                marginBottom: 4,
                cursor: 'pointer',
                background: tab === id ? 'rgba(0,167,220,0.16)' : 'transparent',
                color: tab === id ? '#00a7dc' : 'var(--text2)',
                fontWeight: tab === id ? 800 : 500,
              }}
            >
              {label}
            </button>
          ))}
        </aside>

        <main>
          {tab === 'partner' ? (
            <PartnerForm saving={saving} onSubmit={body => runAction(() => api.post('/partner-saas/organizations', body), 'Партнёр создан')} />
          ) : !org ? (
            <EmptyState />
          ) : tab === 'overview' ? (
            <Overview org={org} products={products} billing={workspace.billing || []} integrations={workspace.integrations || []} />
          ) : tab === 'inventory' ? (
            <Inventory
              products={products}
              warehouses={warehouses}
              inventoryCounts={workspace.inventory_counts || []}
              orgId={org.id}
              saving={saving}
              onProduct={body => runAction(() => api.post('/partner-saas/products', body), 'Товар добавлен')}
              onMovement={body => runAction(() => api.post('/partner-saas/stock-movements', body), 'Складская операция проведена')}
              onCreateInventory={body => runAction(() => api.post('/partner-saas/inventory-counts', body), 'Инвентаризация создана')}
              onUpdateInventoryItem={(countId, itemId, body) => runAction(() => api.patch(`/partner-saas/inventory-counts/${countId}/items/${itemId}`, body), 'Факт сохранён')}
              onCompleteInventory={countId => runAction(() => api.post(`/partner-saas/inventory-counts/${countId}/complete`), 'Инвентаризация завершена')}
            />
          ) : tab === 'sales' ? (
            <Sales
              orgId={org.id}
              stores={stores}
              warehouses={warehouses}
              products={products}
              sales={workspace.sales || []}
              saving={saving}
              onSale={body => runAction(() => api.post('/partner-saas/sales', body), 'Продажа оформлена')}
              onFiscalize={saleId => runAction(() => api.post('/partner-saas/sales/fiscalize', { sale_id: saleId }), 'Чек отправлен на кассу')}
            />
          ) : tab === 'billing' ? (
            <Billing
              org={org}
              billing={workspace.billing || []}
              saving={saving}
              onRun={body => runAction(() => api.post('/partner-saas/billing/run', body), 'Начисление рассчитано')}
              onPayment={body => runAction(() => api.post('/partner-saas/payments', body), 'Платёж зачтён')}
            />
          ) : tab === 'integrations' ? (
            <Integrations
              orgId={org.id}
              integrations={workspace.integrations || []}
              saving={saving}
              onSave={body => runAction(() => api.post('/partner-saas/integrations', body), 'Интеграция сохранена')}
            />
          ) : (
            <Audit items={workspace.audit || []} />
          )}
        </main>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <Section title="Нет выбранного партнёра" sub="Добавь партнёра или создай демо-данные, чтобы открыть рабочее место." />
  );
}

function Overview({ org, products, billing, integrations }) {
  const overdue = billing.filter(b => b.debt > 0);
  return (
    <>
      <Section title={org.name} sub={org.status_reason}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(150px, 1fr))', gap: 12 }}>
          <Info label="Статус доступа" value={<StatusBadge status={org.access_status} />} />
          <Info label="Долг" value={fmt(org.current_debt)} color={org.current_debt > 0 ? '#e04848' : '#0e9f6e'} />
          <Info label="Подписка" value={fmt(org.monthly_subscription)} />
          <Info label="Royalty" value={`${org.royalty_rate}%`} />
        </div>
      </Section>
      <Section title="Контроль удержания" sub="Статус меняется автоматически по просрочке платежей.">
        <div style={{ display: 'grid', gap: 8 }}>
          {['warning', 'limited', 'penalty', 'blocked'].map(status => (
            <div key={status} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--bg3)', borderRadius: 10 }}>
              <StatusBadge status={status} />
              <span style={{ fontSize: 12, color: 'var(--text2)' }}>
                {status === 'warning' && `с ${org.warning_after_days} дня просрочки`}
                {status === 'limited' && `с ${org.limited_after_days} дня: запрет новых операций`}
                {status === 'penalty' && `с ${org.penalty_after_days} дня: тариф x${org.penalty_multiplier}`}
                {status === 'blocked' && `с ${org.blocked_after_days} дня: блокировка`}
              </span>
            </div>
          ))}
        </div>
      </Section>
      <Section title="Быстрая сводка">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          <Info label="Товаров" value={products.length} color="#00a7dc" />
          <Info label="Открытых счетов" value={overdue.length} color={overdue.length ? '#e04848' : '#0e9f6e'} />
          <Info label="Интеграций" value={integrations.length} color="#00a7dc" />
        </div>
      </Section>
    </>
  );
}

function Info({ label, value, color = 'var(--text1)' }) {
  return (
    <div style={{ background: 'var(--bg3)', borderRadius: 10, padding: 12, border: '1px solid var(--border)' }}>
      <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 5 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 900, color }}>{value}</div>
    </div>
  );
}

function PartnerForm({ saving, onSubmit }) {
  const [form, setForm] = useState({
    name: '', legal_name: '', inn: '', contact_name: '',
    monthly_subscription: 25000, royalty_rate: 5, penalty_multiplier: 2,
    due_day: 5, warning_after_days: 1, limited_after_days: 4, penalty_after_days: 8, blocked_after_days: 15,
  });
  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  return (
    <Section title="Новый партнёр" sub="Создаёт организацию, основной магазин и основной склад.">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        <Field label="Название"><input style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} /></Field>
        <Field label="Юр. лицо"><input style={inputStyle} value={form.legal_name} onChange={e => set('legal_name', e.target.value)} /></Field>
        <Field label="ИНН"><input style={inputStyle} value={form.inn} onChange={e => set('inn', e.target.value)} /></Field>
        <Field label="Контакт"><input style={inputStyle} value={form.contact_name} onChange={e => set('contact_name', e.target.value)} /></Field>
        <Field label="Подписка"><input type="number" style={inputStyle} value={form.monthly_subscription} onChange={e => set('monthly_subscription', Number(e.target.value))} /></Field>
        <Field label="Royalty, %"><input type="number" style={inputStyle} value={form.royalty_rate} onChange={e => set('royalty_rate', Number(e.target.value))} /></Field>
        <Field label="Штрафной множитель"><input type="number" style={inputStyle} value={form.penalty_multiplier} onChange={e => set('penalty_multiplier', Number(e.target.value))} /></Field>
        <Field label="День оплаты"><input type="number" style={inputStyle} value={form.due_day} onChange={e => set('due_day', Number(e.target.value))} /></Field>
        <Field label="Блокировка с дня"><input type="number" style={inputStyle} value={form.blocked_after_days} onChange={e => set('blocked_after_days', Number(e.target.value))} /></Field>
      </div>
      <button disabled={saving || !form.name} onClick={() => onSubmit(form)} style={{ ...buttonStyle, marginTop: 14 }}>Создать партнёра</button>
    </Section>
  );
}

function Inventory({
  orgId,
  products,
  warehouses,
  inventoryCounts,
  saving,
  onProduct,
  onMovement,
  onCreateInventory,
  onUpdateInventoryItem,
  onCompleteInventory,
}) {
  const [product, setProduct] = useState({ name: '', sku: '', barcode: '', description: '', photo_url: '', category: '', cost_price: 0, sale_price: 0 });
  const [movement, setMovement] = useState({ warehouse_id: '', product_id: '', movement_type: 'receipt', quantity: 1, unit_cost: 0, comment: '' });
  const [importing, setImporting] = useState(false);
  const [importInfo, setImportInfo] = useState('');
  const setP = (key, value) => setProduct(prev => ({ ...prev, [key]: value }));
  const setM = (key, value) => setMovement(prev => ({ ...prev, [key]: value }));

  const printLabel = p => {
    const safe = s => String(s || '').replace(/[<>&"]/g, ch => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' }[ch]));
    const html = `
      <html><head><title>Бирка ${safe(p.name)}</title>
      <style>
        @page { size: 58mm 40mm; margin: 3mm; }
        body { font-family: Arial, sans-serif; margin: 0; }
        .label { width: 52mm; height: 34mm; border: 1px solid #111; padding: 3mm; box-sizing: border-box; display: grid; align-content: space-between; }
        .name { font-size: 11px; font-weight: 700; line-height: 1.15; }
        .sku { font-size: 9px; color: #333; }
        .price { font-size: 17px; font-weight: 900; text-align: right; }
        .photo { width: 14mm; height: 14mm; object-fit: cover; float: right; margin-left: 2mm; }
      </style></head><body>
        <div class="label">
          <div>
            ${p.photo_url ? `<img class="photo" src="${safe(p.photo_url)}" />` : ''}
            <div class="name">${safe(p.name)}</div>
            <div class="sku">Арт: ${safe(p.sku || p.external_id || '-')}</div>
            ${p.barcode ? `<div class="sku">ШК: ${safe(p.barcode)}</div>` : ''}
          </div>
          <div class="price">${fmt(p.sale_price)}</div>
        </div>
        <script>window.print(); setTimeout(() => window.close(), 500);</script>
      </body></html>`;
    const win = window.open('', '_blank', 'width=420,height=320');
    if (win) {
      win.document.write(html);
      win.document.close();
    }
  };

  const importFromOnec = async () => {
    setImporting(true);
    setImportInfo('Загружаю базу артикулов из 1С...');
    try {
      const stock = await api.get('/onec/stock', { timeout: 300000 }).then(r => r.data);
      const items = (stock.items || []).map(x => ({
        nom_key: x.nom_key || '',
        article: x.article || '',
        name: x.name || '',
        description: x.description || x.name || '',
        photo_url: x.photo_url || '',
      }));
      const res = await api.post('/partner-saas/products/import', {
        organization_id: orgId,
        sync_stock: false,
        items,
      }, { timeout: 300000 }).then(r => r.data);
      setImportInfo(`База обновлена: новых артикулов ${res.imported}, обновлено ${res.updated}, пропущено ${res.skipped}. Остатки филиала не менялись.`);
    } catch (e) {
      setImportInfo(e.response?.data?.detail || e.message || 'Ошибка импорта из 1С');
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
      <Section title="База артикулов Академии" sub="Загружает только артикул, описание и фото из 1С. Остатки мастера не трогаются.">
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button disabled={saving || importing} onClick={importFromOnec} style={{ ...buttonStyle, background: '#0e9f6e' }}>
            {importing ? 'Загружаю...' : 'Подтянуть базу артикулов из 1С'}
          </button>
          <span style={{ color: 'var(--text2)', fontSize: 12 }}>
            Мастер сможет выбрать ваш артикул из базы или создать свой локальный товар вручную.
          </span>
        </div>
        {importInfo && <div style={{ marginTop: 10, color: importInfo.includes('Ошибка') ? '#e04848' : '#0e9f6e', fontSize: 12 }}>{importInfo}</div>}
      </Section>
      <Section title="Товары" sub="Каталог и текущие остатки по всем складам.">
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
          <Field label="Название"><input style={inputStyle} value={product.name} onChange={e => setP('name', e.target.value)} /></Field>
          <Field label="SKU"><input style={inputStyle} value={product.sku} onChange={e => setP('sku', e.target.value)} /></Field>
          <Field label="Категория"><input style={inputStyle} value={product.category} onChange={e => setP('category', e.target.value)} /></Field>
          <Field label="Себестоимость"><input type="number" style={inputStyle} value={product.cost_price} onChange={e => setP('cost_price', Number(e.target.value))} /></Field>
          <Field label="Цена"><input type="number" style={inputStyle} value={product.sale_price} onChange={e => setP('sale_price', Number(e.target.value))} /></Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10, marginBottom: 12 }}>
          <Field label="Описание"><input style={inputStyle} value={product.description} onChange={e => setP('description', e.target.value)} placeholder="Описание товара" /></Field>
          <Field label="Фото URL"><input style={inputStyle} value={product.photo_url} onChange={e => setP('photo_url', e.target.value)} placeholder="https://..." /></Field>
        </div>
        <button disabled={saving || !product.name} onClick={() => onProduct({ ...product, organization_id: orgId })} style={buttonStyle}>Добавить товар</button>
        <SimpleTable
          style={{ marginTop: 14 }}
          columns={['Фото', 'Товар', 'SKU', 'Источник', 'Цена', 'Остаток', 'Бирка']}
          rows={products.map(p => [
            p.photo_url ? <img src={p.photo_url} alt="" style={{ width: 42, height: 42, objectFit: 'cover', borderRadius: 6 }} /> : '-',
            p.name,
            p.sku || '-',
            p.source === '1c_academy' ? 'База Академии' : 'Свой товар мастера',
            fmt(p.sale_price),
            p.stock || 0,
            <button onClick={() => printLabel(p)} style={{ ...buttonStyle, padding: '6px 10px', fontSize: 11 }}>Печать</button>,
          ])}
        />
      </Section>
      <Section title="Складская операция" sub="Приход, возврат, списание или корректировка.">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
          <Field label="Склад"><select style={inputStyle} value={movement.warehouse_id} onChange={e => setM('warehouse_id', Number(e.target.value))}><option value="">Выбери</option>{warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}</select></Field>
          <Field label="Товар"><select style={inputStyle} value={movement.product_id} onChange={e => setM('product_id', Number(e.target.value))}><option value="">Выбери</option>{products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></Field>
          <Field label="Тип"><select style={inputStyle} value={movement.movement_type} onChange={e => setM('movement_type', e.target.value)}><option value="receipt">Приход</option><option value="return">Возврат</option><option value="writeoff">Списание</option><option value="adjustment">Корректировка</option></select></Field>
          <Field label="Кол-во"><input type="number" style={inputStyle} value={movement.quantity} onChange={e => setM('quantity', Number(e.target.value))} /></Field>
          <Field label="Цена учета"><input type="number" style={inputStyle} value={movement.unit_cost} onChange={e => setM('unit_cost', Number(e.target.value))} /></Field>
        </div>
        <button
          disabled={saving || !movement.warehouse_id || !movement.product_id}
          onClick={() => onMovement({ ...movement, organization_id: orgId })}
          style={{ ...buttonStyle, marginTop: 14 }}
        >
          Провести операцию
        </button>
      </Section>
      <InventoryCounts
        orgId={orgId}
        warehouses={warehouses}
        inventoryCounts={inventoryCounts}
        saving={saving}
        onCreate={onCreateInventory}
        onUpdateItem={onUpdateInventoryItem}
        onComplete={onCompleteInventory}
      />
    </>
  );
}

function InventoryCounts({ orgId, warehouses, inventoryCounts, saving, onCreate, onUpdateItem, onComplete }) {
  const [warehouseId, setWarehouseId] = useState(warehouses[0]?.id || '');
  const [name, setName] = useState('Плановая инвентаризация');
  const [draft, setDraft] = useState({});
  const active = inventoryCounts.find(c => c.status !== 'completed') || inventoryCounts[0];
  const setFact = (itemId, value) => setDraft(prev => ({ ...prev, [itemId]: value }));

  return (
    <Section title="Инвентаризация" sub="Создай документ, внеси фактические остатки и заверши — система сама сделает корректировки.">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, alignItems: 'end', marginBottom: 14 }}>
        <Field label="Склад"><select style={inputStyle} value={warehouseId} onChange={e => setWarehouseId(Number(e.target.value))}><option value="">Выбери</option>{warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}</select></Field>
        <Field label="Название"><input style={inputStyle} value={name} onChange={e => setName(e.target.value)} /></Field>
        <button
          disabled={saving || !warehouseId}
          onClick={() => onCreate({ organization_id: orgId, warehouse_id: Number(warehouseId), name })}
          style={buttonStyle}
        >
          Создать пересчёт
        </button>
      </div>

      {!active ? (
        <div style={{ color: 'var(--text2)', fontSize: 12 }}>Инвентаризаций пока нет.</div>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 10 }}>
            <div>
              <div style={{ fontWeight: 900 }}>{active.doc_number} · {active.name}</div>
              <div style={{ color: 'var(--text2)', fontSize: 12 }}>Статус: {active.status} · строк: {active.items?.length || 0}</div>
            </div>
            <button
              disabled={saving || active.status === 'completed'}
              onClick={() => onComplete(active.id)}
              style={{ ...buttonStyle, background: '#0e9f6e' }}
            >
              Завершить и скорректировать
            </button>
          </div>
          <SimpleTable
            columns={['Товар', 'SKU', 'Учёт', 'Факт', 'Разница', 'Сохранить']}
            rows={(active.items || []).slice(0, 80).map(item => {
              const value = draft[item.id] ?? item.actual_quantity ?? '';
              const fact = value === '' ? null : Number(value);
              const diff = fact === null ? item.difference : fact - (item.expected_quantity || 0);
              return [
                item.name,
                item.sku || '-',
                item.expected_quantity || 0,
                <input type="number" style={{ ...inputStyle, minWidth: 90 }} value={value} onChange={e => setFact(item.id, e.target.value)} disabled={active.status === 'completed'} />,
                diff || 0,
                <button
                  disabled={saving || active.status === 'completed' || value === ''}
                  onClick={() => onUpdateItem(active.id, item.id, { actual_quantity: Number(value) })}
                  style={{ ...buttonStyle, padding: '6px 10px', fontSize: 11 }}
                >
                  OK
                </button>,
              ];
            })}
          />
          {(active.items || []).length > 80 && (
            <div style={{ marginTop: 8, color: 'var(--text2)', fontSize: 12 }}>
              Показаны первые 80 строк. Для полной ревизии лучше фильтровать по категории/стеллажу следующим шагом.
            </div>
          )}
        </>
      )}
    </Section>
  );
}

function Sales({ orgId, stores, warehouses, products, sales, saving, onSale, onFiscalize }) {
  const [form, setForm] = useState({
    store_id: '',
    warehouse_id: '',
    product_id: '',
    quantity: 1,
    unit_price: 0,
    payment_method: 'card',
    fiscalize: true,
    customer_phone: '',
    customer_email: '',
  });
  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  useEffect(() => {
    if (!form.unit_price && form.product_id) {
      const p = products.find(item => String(item.id) === String(form.product_id));
      if (p) set('unit_price', p.sale_price || 0);
    }
  }, [form.product_id]);
  return (
    <>
      <Section title="Новая продажа" sub="Продажа списывает остаток и сразу отправляет чек на 57Ф, как сейчас делает МойСклад.">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 10 }}>
          <Field label="Магазин"><select style={inputStyle} value={form.store_id} onChange={e => set('store_id', Number(e.target.value))}><option value="">Выбери</option>{stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></Field>
          <Field label="Склад"><select style={inputStyle} value={form.warehouse_id} onChange={e => set('warehouse_id', Number(e.target.value))}><option value="">Выбери</option>{warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}</select></Field>
          <Field label="Товар"><select style={inputStyle} value={form.product_id} onChange={e => set('product_id', Number(e.target.value))}><option value="">Выбери</option>{products.map(p => <option key={p.id} value={p.id}>{p.name} ({p.stock || 0})</option>)}</select></Field>
          <Field label="Кол-во"><input type="number" style={inputStyle} value={form.quantity} onChange={e => set('quantity', Number(e.target.value))} /></Field>
          <Field label="Цена"><input type="number" style={inputStyle} value={form.unit_price} onChange={e => set('unit_price', Number(e.target.value))} /></Field>
          <Field label="Оплата"><select style={inputStyle} value={form.payment_method} onChange={e => set('payment_method', e.target.value)}><option value="card">Эквайринг</option><option value="cash">Наличные</option><option value="mixed">Смешанная</option></select></Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, marginTop: 10, alignItems: 'end' }}>
          <Field label="Телефон для электронного чека"><input style={inputStyle} value={form.customer_phone} onChange={e => set('customer_phone', e.target.value)} placeholder="+7..." /></Field>
          <Field label="Email для электронного чека"><input style={inputStyle} value={form.customer_email} onChange={e => set('customer_email', e.target.value)} placeholder="client@mail.ru" /></Field>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text2)', fontSize: 12, paddingBottom: 10 }}>
            <input type="checkbox" checked={form.fiscalize} onChange={e => set('fiscalize', e.target.checked)} />
            Пробить чек на 57Ф
          </label>
        </div>
        <button
          disabled={saving || !form.store_id || !form.warehouse_id || !form.product_id}
          onClick={() => onSale({
            organization_id: orgId,
            store_id: form.store_id,
            warehouse_id: form.warehouse_id,
            payment_method: form.payment_method,
            fiscalize: form.fiscalize,
            customer_phone: form.customer_phone,
            customer_email: form.customer_email,
            items: [{ product_id: form.product_id, quantity: form.quantity, unit_price: form.unit_price }],
          })}
          style={{ ...buttonStyle, marginTop: 14 }}
        >
          Оформить продажу
        </button>
      </Section>
      <Section title="Последние продажи">
        <SimpleTable
          columns={['Номер', 'Дата', 'Сумма', 'Оплата', 'Касса 57Ф', 'ФД/ФПД', 'Действие']}
          rows={sales.map(s => [
            s.sale_number,
            (s.created_at || '').slice(0, 16).replace('T', ' '),
            fmt(s.total_amount),
            s.payment_method,
            s.fiscal_error ? `${s.fiscal_status}: ${s.fiscal_error}` : s.fiscal_status,
            [s.fiscal_fd, s.fiscal_fpd].filter(Boolean).join(' / ') || '-',
            s.fiscal_status === 'done'
              ? 'Чек пробит'
              : <button disabled={saving} onClick={() => onFiscalize(s.id)} style={{ ...buttonStyle, padding: '6px 10px', fontSize: 11 }}>Повторить</button>,
          ])}
        />
      </Section>
    </>
  );
}

function Billing({ org, billing, saving, onRun, onPayment }) {
  const currentPeriod = new Date().toISOString().slice(0, 7);
  const [period, setPeriod] = useState(currentPeriod);
  const [payment, setPayment] = useState({ amount: 0, billing_period_id: '' });
  return (
    <>
      <Section title="Начисления" sub="Система считает подписку + royalty. В режиме penalty применяется повышающий множитель.">
        <div style={{ display: 'flex', gap: 10, alignItems: 'end', marginBottom: 14 }}>
          <Field label="Период"><input style={inputStyle} value={period} onChange={e => setPeriod(e.target.value)} placeholder="YYYY-MM" /></Field>
          <button disabled={saving} onClick={() => onRun({ organization_id: org.id, period })} style={buttonStyle}>Рассчитать</button>
        </div>
        <SimpleTable
          columns={['Период', 'Оборот', 'Подписка', 'Royalty', 'Штраф', 'К оплате', 'Оплачено', 'Долг', 'Статус']}
          rows={billing.map(b => [b.period, fmt(b.turnover), fmt(b.subscription_amount), fmt(b.royalty_amount), fmt(b.penalty_amount), fmt(b.total_due), fmt(b.total_paid), fmt(b.debt), b.status])}
        />
      </Section>
      <Section title="Зачесть оплату" sub="Платёж гасит выбранный счёт или старейшие долги партнёра.">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, alignItems: 'end' }}>
          <Field label="Счёт"><select style={inputStyle} value={payment.billing_period_id} onChange={e => setPayment(prev => ({ ...prev, billing_period_id: e.target.value }))}><option value="">Старейшие долги</option>{billing.map(b => <option key={b.id} value={b.id}>{b.period} - долг {fmt(b.debt)}</option>)}</select></Field>
          <Field label="Сумма"><input type="number" style={inputStyle} value={payment.amount} onChange={e => setPayment(prev => ({ ...prev, amount: Number(e.target.value) }))} /></Field>
          <button
            disabled={saving || !payment.amount}
            onClick={() => onPayment({ organization_id: org.id, amount: payment.amount, billing_period_id: payment.billing_period_id ? Number(payment.billing_period_id) : null })}
            style={buttonStyle}
          >
            Зачесть
          </button>
        </div>
      </Section>
    </>
  );
}

function Integrations({ orgId, integrations, saving, onSave }) {
  const fiscal57fConfig = {
    mode: 'mock',
    api_url: 'http://127.0.0.1:7777',
    receipt_path: '/receipts',
    timeout: 20,
    default_customer_phone: '',
    default_customer_email: '',
    comment: 'Для реальной 57Ф замените mode на http и укажите URL локального кассового агента/драйвера.',
  };
  const [form, setForm] = useState({
    integration_type: 'cash_register',
    provider: '57f',
    status: 'connected',
    config: JSON.stringify(fiscal57fConfig, null, 2),
  });
  const body = useMemo(() => {
    let config = {};
    try { config = JSON.parse(form.config || '{}'); } catch { config = {}; }
    return { organization_id: orgId, integration_type: form.integration_type, provider: form.provider, status: form.status, config };
  }, [form, orgId]);
  return (
    <Section title="Касса и эквайринг" sub="Настройка повторяет текущую схему МойСклада: продажа в приложении отправляет чек на 57Ф, 57Ф фискализирует и передаёт данные в ОФД/ФНС.">
      <div style={{ marginBottom: 12, padding: '10px 12px', borderRadius: 10, background: 'rgba(0,167,220,0.10)', border: '1px solid rgba(0,167,220,0.25)', color: 'var(--text2)', fontSize: 12, lineHeight: 1.45 }}>
        Сейчас включён безопасный режим <b style={{ color: '#00a7dc' }}>mock</b>: можно тестировать продажи без физической кассы. Для боевой точки нужен локальный кассовый агент рядом с 57Ф и режим <b style={{ color: '#00a7dc' }}>http</b>.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
        <Field label="Тип"><select style={inputStyle} value={form.integration_type} onChange={e => setForm(prev => ({ ...prev, integration_type: e.target.value }))}><option value="acquiring">Эквайринг</option><option value="cash_register">Онлайн-касса</option><option value="fiscal_operator">ОФД</option></select></Field>
        <Field label="Провайдер"><select style={inputStyle} value={form.provider} onChange={e => setForm(prev => ({ ...prev, provider: e.target.value }))}><option value="57f">57Ф</option><option value="atol">АТОЛ</option><option value="evotor">Эвотор</option><option value="manual">Ручной</option></select></Field>
        <Field label="Статус"><select style={inputStyle} value={form.status} onChange={e => setForm(prev => ({ ...prev, status: e.target.value }))}><option value="draft">Черновик</option><option value="connected">Подключено</option><option value="error">Ошибка</option><option value="disabled">Отключено</option></select></Field>
      </div>
      <Field label="JSON настройки"><textarea style={{ ...inputStyle, minHeight: 72, marginTop: 10 }} value={form.config} onChange={e => setForm(prev => ({ ...prev, config: e.target.value }))} /></Field>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button disabled={saving} onClick={() => onSave(body)} style={buttonStyle}>Сохранить интеграцию</button>
        <button
          disabled={saving}
          onClick={() => setForm({
            integration_type: 'cash_register',
            provider: '57f',
            status: 'connected',
            config: JSON.stringify(fiscal57fConfig, null, 2),
          })}
          style={{ ...buttonStyle, background: 'var(--bg3)', color: 'var(--text1)', border: '1px solid var(--border)' }}
        >
          Шаблон 57Ф
        </button>
      </div>
      <SimpleTable
        style={{ marginTop: 14 }}
        columns={['Тип', 'Провайдер', 'Статус', 'Настройки']}
        rows={integrations.map(i => [i.integration_type, i.provider || '-', i.status, JSON.stringify(i.config || {})])}
      />
    </Section>
  );
}

function Audit({ items }) {
  return (
    <Section title="Журнал аудита" sub="Критичные изменения по партнёру, тарифам, начислениям и оплатам.">
      <SimpleTable
        columns={['Дата', 'Пользователь', 'Действие', 'Детали']}
        rows={items.map(i => [(i.created_at || '').slice(0, 16).replace('T', ' '), i.actor, i.action, JSON.stringify(i.details || {})])}
      />
    </Section>
  );
}

function SimpleTable({ columns, rows, style = {} }) {
  return (
    <div style={{ overflowX: 'auto', ...style }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            {columns.map(c => <th key={c} style={{ textAlign: 'left', color: 'var(--text2)', padding: '9px 8px', borderBottom: '1px solid var(--border)' }}>{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.length ? rows.map((row, idx) => (
            <tr key={idx}>
              {row.map((cell, cidx) => <td key={cidx} style={{ padding: '9px 8px', borderBottom: '1px solid var(--border)', color: 'var(--text1)' }}>{cell}</td>)}
            </tr>
          )) : (
            <tr><td colSpan={columns.length} style={{ padding: 18, color: 'var(--text2)', textAlign: 'center' }}>Нет данных</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
